import { CheckCircleIcon, UserCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import type { Company, ReferentMutation } from '../../../../@types/Company';
import type { User } from '../../../../@types/User';
import type { RootState } from '../../../../store/store';
import { getSalaries } from '../../../../utils/api';
import { JWTHeader } from '../../../../utils/JWTHeader';
import PreviewViewer from '../../../common/PreviewViewer/PreviewViewer.module';

interface SummaryStepProps {
  draft: NonNullable<Company['brandingSettings']>;
  company: Company;
  onEditReportPresentation?: () => void;
}

function SummaryStep({ draft, company, onEditReportPresentation }: SummaryStepProps) {
  const jwt = useSelector((s: RootState) => s.auth.token);

  const [latestNewsletter, setLatestNewsletter] = useState<{ date: string; slug: string } | null>(null);
  const [selectedTemplatePageCount, setSelectedTemplatePageCount] = useState<number | null>(null);
  const [usersMap, setUsersMap] = useState<Map<number, User>>(new Map());

  const referents: ReferentMutation[] = useMemo(() => {
    return draft.referents || [];
  }, [draft.referents]);

  const selectedTemplate = draft.defaultTemplate;

  const persistedReferents = company.brandingSettings?.referents ?? referents;
  const referentsWithPdf = persistedReferents.filter((referent) => Boolean(referent.pdfIntroductionName?.trim()));
  const referentsWithoutPdf = persistedReferents.filter((referent) => !referent.pdfIntroductionName?.trim());

  const validReferentsForReports = persistedReferents.filter(
    (referent) => referent.title?.trim() && referent.firstname?.trim() && referent.lastname?.trim(),
  );

  const sharedPresentationPaths = useMemo(() => {
    const paths = referentsWithPdf
      .map((referent) => referent.pdfIntroductionName?.trim())
      .filter(Boolean) as string[];
    return new Set(paths);
  }, [referentsWithPdf]);

  const isSharedPresentation = sharedPresentationPaths.size === 1
    && referentsWithPdf.length === validReferentsForReports.length
    && validReferentsForReports.length > 0;

  const getReferentDisplayName = (referent: ReferentMutation): string => (
    [referent.title, referent.firstname, referent.lastname].filter(Boolean).join(' ')
  );

  useEffect(() => {
    if (!company?.id || !jwt) { return; }
    fetch(getSalaries(company.id, false), JWTHeader(jwt))
      .then((res) => res.json())
      .then((data) => {
        if (data?.results) {
          const map = new Map<number, User>();
          data.results.forEach((f: { user?: User }) => { if (f.user) { map.set(f.user.id, f.user); } });
          setUsersMap(map);
        }
      })
      .catch(() => {});
  }, [company?.id, jwt]);

  useEffect(() => {
    let cancelled = false;
    const loadLatestAndTemplate = async() => {
      try {
        if (!jwt || !selectedTemplate || !company?.id) { return; }
        const response = await fetch(`/api/companies/${company.id}/fixed-newsletters`, JWTHeader(jwt));
        if (!response.ok) { return; }
        const data = await response.json();
        const oldest = Array.isArray(data) && data.length > 0 ? data[0] : null;
        if (!oldest) { return; }
        if (cancelled) { return; }
        setLatestNewsletter({ date: oldest.date, slug: oldest.slug });
        const tpl = oldest.templates?.find((t: { name: string; pageCount?: number }) => t.name === selectedTemplate);
        setSelectedTemplatePageCount(tpl ? Number(tpl.pageCount) : null);
      } catch {
        // silent fail: preview is optional in summary
      }
    };
    loadLatestAndTemplate();
    return () => { cancelled = true; };
  }, [jwt, selectedTemplate]);

  const canShowTemplatePreview = useMemo(() => (
    Boolean(latestNewsletter && selectedTemplate && selectedTemplatePageCount && selectedTemplatePageCount > 0)
  ), [latestNewsletter, selectedTemplate, selectedTemplatePageCount]);

  return (
    <div>
      <h3 className="mb-4 text-lg font-semibold text-gray-800">Récapitulatif</h3>

      <div className="mb-6 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1">
            <p className="font-medium text-indigo-900">Présentation de l&apos;entreprise pour les rapports</p>
            <p className="mt-2 text-sm leading-relaxed text-indigo-800">
              Ce PDF optionnel constitue la première section de vos rapports générés (copropriété, DPE, ventes, valorisation).
              Il présente votre entreprise avant le contenu du rapport. La version utilisée dépend du référent publique
              assigné à chaque copropriété.
            </p>
            {referentsWithPdf.length > 0 ? (
              <p className="mt-2 text-sm font-medium text-indigo-900">
                {isSharedPresentation
                  ? 'Une présentation commune est configurée pour tous vos référents.'
                  : `${referentsWithPdf.length} référent(s) sur ${validReferentsForReports.length} disposent d'une présentation dédiée.`}
              </p>
            ) : (
              <p className="mt-2 text-sm text-indigo-700">
                Aucune présentation n&apos;est configurée pour l&apos;instant — cette étape reste facultative.
              </p>
            )}
          </div>
          {onEditReportPresentation && (
            <button
              type="button"
              onClick={onEditReportPresentation}
              className="shrink-0 rounded-lg border border-indigo-300 bg-white px-4 py-2 text-sm font-semibold text-indigo-800 transition-colors hover:bg-indigo-100"
            >
              {referentsWithPdf.length > 0 ? 'Modifier la présentation' : 'Configurer la présentation'}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="col-span-1 rounded border p-4 md:col-span-2">
          <div className="mb-2 font-medium text-gray-800">Identité affichée sur le blog</div>
          <p className="text-sm text-gray-700">{draft.commercialName?.trim() || company.name}</p>
          <p className="mt-1 text-sm text-gray-600">
            {(draft.showLegalInfoOnBlog ?? true) ? 'Nom légal et adresse affichés' : 'Nom commercial uniquement'}
          </p>
        </div>
        <div className="rounded border p-4">
          <div className="mb-2 font-medium text-gray-800">Couleurs</div>
          <div className="flex items-center gap-3">
            <span className="h-6 w-6 rounded" style={{ backgroundColor: draft.color1 }} />
            <span className="text-sm text-gray-700">{draft.color1}</span>
          </div>
          <div className="mt-2 flex items-center gap-3">
            <span className="h-6 w-6 rounded" style={{ backgroundColor: draft.color2 }} />
            <span className="text-sm text-gray-700">{draft.color2}</span>
          </div>
          <div className="mt-2 flex items-center gap-3">
            <span className="h-6 w-6 rounded" style={{ backgroundColor: draft.color3 }} />
            <span className="text-sm text-gray-700">{draft.color3}</span>
          </div>
        </div>
        <div className="rounded border p-4">
          <div className="mb-3 font-medium text-gray-800">Logos</div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col items-center gap-2 rounded border border-gray-100 bg-gray-50 p-3">
              <div className="text-xs font-medium text-gray-600">Logo principal (paysage)</div>
              <div className="text-[10px] text-gray-400">Newsletters &amp; communications</div>
              {company.brandingSettings?.brandingImageName ? (
                <img
                  src={company.brandingSettings.brandingImageName}
                  alt="Logo principal"
                  className="h-16 w-32 rounded bg-white object-contain p-1"
                />
              ) : (
                <div className="flex h-16 w-32 items-center justify-center rounded bg-white text-xs text-gray-400">
                  Aucun logo principal
                </div>
              )}
            </div>
            <div className="flex flex-col items-center gap-2 rounded border border-gray-100 bg-gray-50 p-3">
              <div className="text-xs font-medium text-gray-600">Logo blog (carré)</div>
              <div className="text-[10px] text-gray-400">Blog public</div>
              {company.brandingSettings?.brandingBlogImageName ? (
                <img
                  src={company.brandingSettings.brandingBlogImageName}
                  alt="Logo blog"
                  className="h-20 w-20 rounded border bg-white object-cover"
                />
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded border bg-white text-xs text-gray-400">
                  Aucun logo blog
                </div>
              )}
            </div>
          </div>
          {(company.brandingSettings?.brandingImageName && !company.brandingSettings?.brandingBlogImageName) && (
            <p className="mt-3 text-xs text-amber-600">
              Astuce : ajoutez un logo carré pour un meilleur rendu sur votre blog public.
            </p>
          )}
          {(!company.brandingSettings?.brandingImageName && company.brandingSettings?.brandingBlogImageName) && (
            <p className="mt-3 text-xs text-amber-600">
              Astuce : ajoutez un logo principal (paysage) pour vos newsletters et communications.
            </p>
          )}
        </div>
        <div className="col-span-1 rounded border p-4 md:col-span-2">
          <div className="mb-2 font-medium text-gray-800">
            Référents mutation/location ({referents.length})
          </div>
          {referents.length === 0 ? (
            <div className="text-sm text-gray-500">Aucun référent configuré</div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {referents.map((referent) => (
                <div key={referent.id} className="flex items-center gap-3 rounded border bg-gray-50 p-3">
                  {referent.photoName ? (
                    <img 
                      src={referent.photoName} 
                      alt={`Photo de ${referent.firstname} ${referent.lastname}`} 
                      className="h-12 w-12 rounded-full border object-cover" 
                    />
                  ) : (
                    <div className="flex h-12 w-12 items-center justify-center rounded-full border bg-gray-200 text-xs text-gray-400">
                      N/A
                    </div>
                  )}
                  <div className="flex-1 text-sm text-gray-700">
                    <div className="font-medium">
                      {referent.title || '(civilité)'} {referent.firstname || '(prénom)'} {referent.lastname || '(nom)'}
                    </div>
                    {referent.email && (
                      <div className="text-xs text-gray-500">{referent.email}</div>
                    )}
                    {referent.phone && (
                      <div className="text-xs text-gray-500">{referent.phone}</div>
                    )}
                    {referent.userId ? (
                      <div className="mt-1 flex items-center gap-1 text-xs text-blue-600">
                        <UserCircleIcon className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">
                          {usersMap.get(referent.userId)
                            ? `${usersMap.get(referent.userId)!.firstname} ${usersMap.get(referent.userId)!.lastname?.toUpperCase() ?? ''}`
                            : `Utilisateur #${referent.userId}`}
                        </span>
                      </div>
                    ) : (
                      <div className="mt-1 text-xs text-gray-400">Aucun utilisateur lié</div>
                    )}
                    {referent.hasExternalNewsletter && referent.externalNewsletterUrl && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-blue-600">
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        <span className="truncate" title={referent.externalNewsletterUrl}>
                          Newsletter externe : {referent.externalNewsletterUrl}
                        </span>
                      </div>
                    )}
                    {referent.hasRgpdBlogArticle && referent.rgpdBlogArticleUrl && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-blue-600">
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        <span className="truncate" title={referent.rgpdBlogArticleUrl}>
                          Article de blog RGPD : {referent.rgpdBlogArticleUrl}
                        </span>
                      </div>
                    )}
                    {(() => {
                      const persisted = persistedReferents.find((r) => r.id === referent.id);
                      const hasPdf = Boolean(persisted?.pdfIntroductionName?.trim());
                      return (
                        <div className={`mt-2 flex items-center gap-1 text-xs ${hasPdf ? 'text-green-700' : 'text-gray-400'}`}>
                          {hasPdf ? (
                            <CheckCircleIcon className="h-3.5 w-3.5 shrink-0" />
                          ) : (
                            <XCircleIcon className="h-3.5 w-3.5 shrink-0" />
                          )}
                          <span>
                            Présentation rapport :
                            {' '}
                            {hasPdf ? 'configurée' : 'non configurée'}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="mt-3 text-xs text-gray-500">
            Un PDF sera généré pour chaque référent lors de l'aperçu de la newsletter.
          </p>
        </div>
        <div className="rounded border p-4">
          <div className="mb-2 font-medium text-gray-800">Sous-traitant mails</div>
          <div className="text-sm text-gray-700">Sous-traitant d'envoi de vos mails : <span className="font-medium">{draft.emailSubcontractorName || '—'}</span></div>
        </div>
        <div className="rounded border p-4">
          <div className="mb-2 font-medium text-gray-800">Newsletter</div>
          {canShowTemplatePreview && latestNewsletter && selectedTemplate && selectedTemplatePageCount ? (
            <div className="max-w-[400px]">
              <div className="aspect-[3/4] overflow-hidden rounded border">
                <PreviewViewer
                  date={latestNewsletter.date}
                  slug={latestNewsletter.slug}
                  pageCount={selectedTemplatePageCount}
                  templateName={selectedTemplate}
                  className="h-full w-full"
                  showNavigation={true}
                />
              </div>
            </div>
          ) : null}
        </div>
        <div className="col-span-1 rounded border p-4 md:col-span-2">
          <div className="mb-2 font-medium text-gray-800">Présentation de l&apos;entreprise pour les rapports (optionnel)</div>
          <p className="mb-4 text-sm text-gray-600">
            Récapitulatif par référent publique : chaque présentation PDF sera insérée en tête des rapports
            des copropriétés qui lui sont associées.
          </p>
          {validReferentsForReports.length === 0 ? (
            <div className="text-sm text-gray-500">Aucun référent configuré</div>
          ) : referentsWithPdf.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-green-700">
                <CheckCircleIcon className="h-5 w-5 shrink-0" />
                <span>
                  {referentsWithPdf.length}
                  {' '}
                  référent(s) avec une présentation entreprise configurée
                  {isSharedPresentation && ' (présentation commune)'}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {validReferentsForReports.map((referent) => {
                  const hasPdf = Boolean(referent.pdfIntroductionName?.trim());
                  return (
                    <div
                      key={referent.id}
                      className={`flex items-center gap-2 rounded border px-3 py-2 text-sm ${
                        hasPdf ? 'border-green-200 bg-green-50 text-green-800' : 'border-gray-200 bg-gray-50 text-gray-600'
                      }`}
                    >
                      {hasPdf ? (
                        <CheckCircleIcon className="h-4 w-4 shrink-0" />
                      ) : (
                        <XCircleIcon className="h-4 w-4 shrink-0 text-gray-400" />
                      )}
                      <span>{getReferentDisplayName(referent)}</span>
                    </div>
                  );
                })}
              </div>
              {referentsWithoutPdf.length > 0 && (
                <p className="text-xs text-amber-700">
                  {referentsWithoutPdf.length}
                  {' '}
                  référent(s) sans présentation : les rapports de leurs copropriétés n&apos;auront pas de section
                  d&apos;introduction entreprise, sauf si un autre référent est utilisé en secours.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <XCircleIcon className="h-5 w-5 shrink-0 text-gray-400" />
                <span>Aucune présentation entreprise configurée (facultatif)</span>
              </div>
              <p className="text-xs text-gray-500">
                Vous pourrez en ajouter une à tout moment depuis cette étape ou depuis les pages de génération de rapports.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SummaryStep;

